import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { Icon } from 'semantic-ui-react';
import { omit, map, findIndex, get } from 'lodash';

import { Modal, Button } from '../../../../components';
import CabinetForm from '../form/CabinetForm';
import { GET_CABINETS_QUERY } from '../../containers/CabinetList';

const intlMessages = defineMessages({
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

export const UPDATE_CABINET_MUTATION = gql`
    mutation updateCabinet($input: UpdateCabinetInput!, $lang: String!) {
        updateCabinet(input: $input) {
            cabinet {
                id
                name(lang: $lang)
                nameVariants {
                    en
                    ru
                    lv
                }
                serviceGroups {
                    id
                    name(lang: $lang)
                    type
                }
            }
        }
    }
`;

function buildInitialValues(cabinet) {
    return {
        nameVariants: omit(cabinet.nameVariants, ['__typename']),
        serviceGroups: map(cabinet.serviceGroups, sg => sg.id),
    };
}

function CabinetDetails({ cabinet, trigger, open, onClose, intl }) {
    const salon = useSelector(({ user }) => user.get('salon'));
    const languages = useSelector(({ intl: i }) => i.get('languages'));
    const defaultLanguage = useSelector(({ intl: i }) => i.get('defaultLanguage'));
    const currentLanguage = useSelector(({ intl: i }) => i.get('locale'));
    const searchQuery = useSelector(({ cabinets }) => cabinets?.get?.('searchQuery') || '');

    const methods = useForm({ defaultValues: buildInitialValues(cabinet) });
    const { handleSubmit, reset, setError, formState: { isSubmitting, isDirty } } = methods;

    useEffect(() => {
        if (open) {
            reset(buildInitialValues(cabinet));
        }
    }, [cabinet.id, open]); // eslint-disable-line react-hooks/exhaustive-deps

    const [updateCabinet] = useMutation(UPDATE_CABINET_MUTATION, {
        update: (store, response) => {
            const updated = get(response, 'data.updateCabinet.cabinet');
            if (!updated) return;
            try {
                const variables = {
                    salon,
                    search: searchQuery || undefined,
                    lang: currentLanguage,
                };
                const data = store.readQuery({ query: GET_CABINETS_QUERY, variables });
                const idx = findIndex(data.viewer.salon.cabinets, c => c.id === cabinet.id);
                const next = [...data.viewer.salon.cabinets];
                next.splice(idx, 1, updated);
                store.writeQuery({
                    query: GET_CABINETS_QUERY,
                    variables,
                    data: {
                        ...data,
                        viewer: {
                            ...data.viewer,
                            salon: { ...data.viewer.salon, cabinets: next },
                        },
                    },
                });
            } catch (e) {
                // cache not populated — safe to ignore
            }
        },
    });

    const onSubmit = async formData => {
        try {
            await updateCabinet({
                variables: {
                    input: { id: cabinet.id, ...formData },
                    lang: currentLanguage,
                },
                optimisticResponse: {
                    __typename: 'Mutation',
                    updateCabinet: {
                        __typename: 'UpdateCabinetPayload',
                        cabinet: {
                            __typename: 'Cabinet',
                            id: cabinet.id,
                            name: formData.nameVariants[currentLanguage],
                            serviceGroups: map(formData.serviceGroups, id => ({
                                __typename: 'ServiceGroups',
                                id,
                                name: '',
                                type: '',
                            })),
                            ...omit(formData, ['serviceGroups']),
                        },
                    },
                },
            });
            onClose();
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (graphQLError) {
                setError('root', { message: graphQLError.message });
                return;
            }
            setError('root', { message: intl.formatMessage(intlMessages.defaultErrorMessage) });
        }
    };

    return (
        <FormProvider {...methods}>
            <Modal trigger={trigger} onClose={onClose} open={open}>
                <Modal.Header>
                    <FormattedMessage
                        id="pages.cabinets.EditCabinet.header"
                        defaultMessage="Edit cabinet"
                    />
                </Modal.Header>
                <Modal.Content>
                    <CabinetForm
                        languages={languages}
                        defaultLanguage={defaultLanguage}
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isSubmitting || !isDirty}
                        loading={isSubmitting}
                        primary
                    >
                        <FormattedMessage
                            id="pages.cabinets.EditCabinet.save"
                            defaultMessage="Save"
                        />
                    </Button>
                    <Button onClick={onClose} disabled={isSubmitting} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.cabinets.EditCabinet.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                </Modal.Actions>
            </Modal>
        </FormProvider>
    );
}

export default injectIntl(CabinetDetails);
